/**
 * Renders position/move nodes and counter interception nodes as instanced
 * meshes.
 *
 * Two non-obvious things that keep picking reliable:
 *  1. Geometry and material are pre-created via useMemo and passed through
 *     `args`, instead of attached via JSX children. Removes any reconciler
 *     race where the geometry isn't on the mesh when the first raycast fires.
 *  2. `boundingSphere` is forced to a huge radius at mount. THREE.Mesh.raycast
 *     does a sphere early-out *before* per-instance tests; otherwise that
 *     sphere is computed once at first raycast and never refreshed, so as the
 *     sim spreads nodes outward the sphere becomes too small and the early-
 *     out rejects every ray. `frustumCulled` does NOT help with this — it
 *     only affects render culling.
 *
 * Per-instance color via the standard InstancedMesh.setColorAt API.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useFrame, type ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import type {
  ClusterBy,
  Graph,
  GraphNode,
  Move,
  Position,
  Zone,
} from '../types/domain';
import {
  ZONE_COLORS,
  BRIDGE_RING,
  ORPHAN_ALPHA,
  COUNTER_COLOR,
  CLUSTER_COLORS,
  CLUSTER_UNCATEGORIZED,
  FOCUS_DIM,
  dimmedValue,
  clusterColorForId,
} from '../zones/palette';
import { useAppStore } from '../state/store';
import type { ForceLayout } from './useForceLayout';
import { clusterIdFor } from '../data/cluster';
import { dragState } from '../state/drag';
// Shared so edge clipping uses the exact same radii as rendered spheres.
import { nodeRadius as radiusFor } from './sizing';
import { advanceBoosts, boostOf } from './interactBoost';

const TMP_MAT = new THREE.Matrix4();
const TMP_QUAT = new THREE.Quaternion();
const TMP_SCALE = new THREE.Vector3();
const TMP_POS = new THREE.Vector3();

const HUGE_SPHERE = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 10000);

function zoneBaseColor(n: GraphNode): THREE.Color {
  if (n.kind === 'interception') return new THREE.Color(COUNTER_COLOR);
  if (n.zone) return new THREE.Color(ZONE_COLORS[n.zone as Zone] ?? '#bbbbbb');
  return new THREE.Color('#bbbbbb');
}

// Open-ended cluster types use a hash-to-color generator because their
// cluster ids are free-form strings (family names, concept tags) instead
// of a small fixed taxonomy.
const HASHED_CLUSTER_TYPES = new Set<ClusterBy>(['family', 'concept']);

function colorFor(
  n: GraphNode,
  clusterBy: ClusterBy,
  positionsById?: Map<string, Position>,
  movesById?: Map<string, Move>,
): THREE.Color {
  // Interception color is fixed across all modes — it represents counter
  // semantics, not a cluster.
  if (n.kind === 'interception') return new THREE.Color(COUNTER_COLOR);
  if (clusterBy === 'none' || !positionsById || !movesById) return zoneBaseColor(n);
  const cid = clusterIdFor(n, clusterBy, positionsById, movesById);
  if (!cid) return new THREE.Color(CLUSTER_UNCATEGORIZED);
  // Fixed-taxonomy clusters: use the curated palette table.
  if (CLUSTER_COLORS[cid]) return new THREE.Color(CLUSTER_COLORS[cid]);
  // Open-ended (family / concept) clusters: derive a deterministic color
  // from the cluster id so each family/concept gets its own distinct tint.
  if (HASHED_CLUSTER_TYPES.has(clusterBy)) {
    return new THREE.Color(
      clusterColorForId(cid, clusterBy as 'family' | 'concept'),
    );
  }
  return new THREE.Color(CLUSTER_UNCATEGORIZED);
}

// Pulse parameters — "small slow pulsation but always on enough to be seen".
// Linear lerp from base color toward white by `glow ∈ [GLOW_MIN, GLOW_MAX]`.
// Both ends halved (~0.40 avg → ~0.20 avg) per user feedback that the
// previous pulse was too bright.
const GLOW_MIN = 0.11;
const GLOW_MAX = 0.29;
const GLOW_BIAS = (GLOW_MIN + GLOW_MAX) / 2;
const GLOW_AMP = (GLOW_MAX - GLOW_MIN) / 2;
const GLOW_HZ = 1.5; // rad/s — full pulse period ~4.2s
// The SELECTED node pulses harder than its merely-adjacent neighbours,
// so "what did I click" is distinct from "what's connected to it".
// Kept just hot enough to read as "selected" without blowing out: the
// Bloom pass spreads with luminance, so a near-white peak threw a wide
// halo/umbra around the node. This peaks modestly over the 0.55 bloom
// threshold instead of far above it — still clearly brighter than the
// neighbour glow (0.11-0.29), much tighter bloom.
const SEL_GLOW_MIN = 0.30;
const SEL_GLOW_MAX = 0.62;
const SEL_GLOW_BIAS = (SEL_GLOW_MIN + SEL_GLOW_MAX) / 2;
const SEL_GLOW_AMP = (SEL_GLOW_MAX - SEL_GLOW_MIN) / 2;
// Slightly faster than the neighbour glow so the two read as different
// rhythms rather than one smeared pulse.
const SEL_GLOW_HZ = 2.1;
// A single dim factor applies whenever an active glow set exists, hover
// or click. The user prefers the strong-focus feel on hover too —
// flickering during mouse-scan is mitigated by the 600ms hover-clear
// debounce. The amount lives in palette.ts as FOCUS_DIM, next to the
// anchor the other dimmed surfaces scale around.
//
// Bridge-ring opacity drops with it, so the outer ring of a non-connected
// node fades along with its sphere — a black sphere sitting inside a
// visible ring reads as a rendering fault rather than a de-emphasis.
//
// The dimmed value is derived rather than written down: 0.08 is where the
// ring was tuned at TUNED_FOCUS_DIM, and dimmedValue carries it to
// whatever FOCUS_DIM currently is. Folded once at module load, so this
// costs nothing per frame and a change to FOCUS_DIM moves the ring with
// the sphere instead of leaving it behind.
const BRIDGE_RING_OPACITY_ACTIVE = 0.5;
const BRIDGE_RING_OPACITY_TUNED = 0.08;
const BRIDGE_RING_OPACITY_DIMMED = dimmedValue(
  BRIDGE_RING_OPACITY_TUNED,
  FOCUS_DIM,
  BRIDGE_RING_OPACITY_ACTIVE,
);
const WHITE = new THREE.Color(1, 1, 1);
const TMP_HL = new THREE.Color();

export function Nodes({
  graph,
  layout,
  excludeId,
  glowNodes,
  additiveGlow = false,
  clusterBy = 'none',
  positionsById,
  movesById,
}: {
  graph: Graph;
  layout: ForceLayout;
  excludeId?: string;
  glowNodes: Set<string>;
  // When true, non-glow nodes keep their base color instead of dimming —
  // used by the gameplan line highlight so the rest of the plan stays lit.
  additiveGlow?: boolean;
  clusterBy?: ClusterBy;
  positionsById?: Map<string, Position>;
  movesById?: Map<string, Move>;
}) {
  // The source of truth for sphere size; every edge/label/marker path
  // reads the same value so geometry stays aligned.
  const sizeBy = useAppStore((s) => s.sizeBy);
  const setHover = useAppStore((s) => s.setHover);
  const clearHoverSoon = useAppStore((s) => s.clearHoverSoon);
  const setSelection = useAppStore((s) => s.setSelection);
  // Read hoverId and selection via refs (not subscriptions) so per-frame
  // matrix updates don't pay a React re-render cost.
  const hoverIdRef = useRef<string | null>(null);
  const selectedIdRef = useRef<string | null>(null);
  useEffect(
    () =>
      useAppStore.subscribe((s) => {
        hoverIdRef.current = s.hoverId;
        const sel = s.selection;
        selectedIdRef.current =
          sel.kind === 'position'
            ? sel.id
            : sel.kind === 'move'
            ? sel.id
            : sel.kind === 'counter'
            ? `intercept:${sel.moveId}:${sel.id}`
            : null;
      }),
    [],
  );

  const positionNodes = useMemo(
    () =>
      graph.nodes.filter(
        (n) => (n.kind === 'position' || n.kind === 'move') && n.id !== excludeId,
      ),
    [graph, excludeId],
  );
  const interceptionNodes = useMemo(
    () => graph.nodes.filter((n) => n.kind === 'interception'),
    [graph],
  );

  // Pre-build geometry + material. Avoids any reconciliation timing surprises.
  const sphereGeom = useMemo(() => new THREE.SphereGeometry(1, 28, 20), []);
  const sphereMat = useMemo(() => new THREE.MeshBasicMaterial({ toneMapped: false }), []);
  // Counter / interception nodes used to render as octahedra to
  // visually distinguish them from the position/move spheres. Per
  // feedback, the octahedron read as a square at most angles and
  // looked alien against the otherwise-spherical scene. They now use
  // the SAME sphere geometry — distinction comes from the warm
  // COUNTER_COLOR and a label rendered alongside (see Scene.tsx, the
  // ProximityLabels now includes interception nodes).
  const intGeom = useMemo(() => new THREE.SphereGeometry(1, 22, 16), []);
  const intMat = useMemo(
    () => new THREE.MeshBasicMaterial({ color: COUNTER_COLOR, toneMapped: false }),
    [],
  );

  const posMeshRef = useRef<THREE.InstancedMesh>(null!);
  const intMeshRef = useRef<THREE.InstancedMesh>(null!);

  // Cache base colors so we can restore them when an instance leaves the
  // glow set (otherwise it'd be stuck pulsing forever). Recompute when
  // clusterBy changes so cluster-tinting takes effect without remounting.
  //
  // Orphans are pre-dimmed at the base-color step (multiplied by
  // ORPHAN_ALPHA) so they read as visually-quiet "isolated stars"
  // regardless of which highlight pass runs later. Radius is no longer
  // used to signal orphan status, so the spheres stay clickable.
  const posBaseColors = useMemo(
    () =>
      positionNodes.map((n) => {
        const c = colorFor(n, clusterBy, positionsById, movesById);
        if (n.isOrphan) c.multiplyScalar(ORPHAN_ALPHA);
        return c;
      }),
    [positionNodes, clusterBy, positionsById, movesById],
  );
  const intBaseColors = useMemo(
    () => interceptionNodes.map(() => new THREE.Color(COUNTER_COLOR)),
    [interceptionNodes],
  );
  // Track previous active state so we can do exactly one final restore pass
  // when the highlight is cleared. Avoids re-uploading instance colors every
  // frame in the idle case.
  const prevActiveRef = useRef(false);

  // Per-instance colors + huge bounding sphere (so the raycaster never short-
  // circuits the per-instance test).
  useEffect(() => {
    if (!posMeshRef.current) return;
    positionNodes.forEach((_, i) => {
      posMeshRef.current.setColorAt(i, posBaseColors[i]);
    });
    if (posMeshRef.current.instanceColor) {
      posMeshRef.current.instanceColor.needsUpdate = true;
    }
    posMeshRef.current.boundingSphere = HUGE_SPHERE;
  }, [positionNodes, posBaseColors]);

  useEffect(() => {
    if (!intMeshRef.current) return;
    const c = new THREE.Color(COUNTER_COLOR);
    interceptionNodes.forEach((_, i) => {
      intMeshRef.current.setColorAt(i, c);
    });
    if (intMeshRef.current.instanceColor) {
      intMeshRef.current.instanceColor.needsUpdate = true;
    }
    intMeshRef.current.boundingSphere = HUGE_SPHERE;
  }, [interceptionNodes]);

  useFrame(({ clock }, dt) => {
    // Drive the shared hover/selection scale for the whole scene. Must
    // run before anything reads boostOf() this frame; labels and bridge
    // rings are read-only consumers of the value settled here.
    advanceBoosts(dt, hoverIdRef.current, selectedIdRef.current);
    if (!layout.posRef.current) return;
    const pos = layout.posRef.current;

    // Hover no longer feeds geometry directly — advanceBoosts owns that
    // now. `selected` is still read below for the colour pass.
    const selected = selectedIdRef.current;
    const hasActive = glowNodes.size > 0;
    const pulse = GLOW_BIAS + GLOW_AMP * Math.sin(clock.elapsedTime * GLOW_HZ);
    const selPulse =
      SEL_GLOW_BIAS + SEL_GLOW_AMP * Math.sin(clock.elapsedTime * SEL_GLOW_HZ);

    // --- Position mesh ---
    if (posMeshRef.current) {
      // Defensive: if the position buffer is smaller than what nodeIndex
      // promises, we'd otherwise read `undefined` slots and produce NaN
      // matrices that poison Three.js's bounding-sphere / raycast state.
      // The useMemo on nodeIndex (see useForceLayout) should keep these in
      // sync; this check is belt-and-suspenders for any tick that fires
      // between a graph swap and the next render.
      const maxIdx = pos.length / 3;
      positionNodes.forEach((n, i) => {
        const idx = layout.nodeIndex.get(n.id);
        if (idx === undefined || idx >= maxIdx) return;
        TMP_POS.set(pos[idx * 3], pos[idx * 3 + 1], pos[idx * 3 + 2]);
        const interactBoost = boostOf(n.id);
        // Radius is the click target — keep it generous regardless
        // of orphan status. Orphans get visually-quieted via a
        // color-dim pass below instead of by shrinking the sphere.
        TMP_SCALE.setScalar(radiusFor(n, sizeBy) * interactBoost);
        TMP_QUAT.identity();
        TMP_MAT.compose(TMP_POS, TMP_QUAT, TMP_SCALE);
        posMeshRef.current.setMatrixAt(i, TMP_MAT);
      });
      posMeshRef.current.instanceMatrix.needsUpdate = true;

      // Three-state color pass: glow / dim / base. When idle (hasActive
      // false), we only need to do a single restore pass once after the
      // active set is cleared — afterwards every instance already holds
      // its base color, so subsequent idle frames skip the work.
      if (hasActive) {
        positionNodes.forEach((n, i) => {
          const base = posBaseColors[i];
          if (selected === n.id) {
            TMP_HL.copy(base).lerp(WHITE, selPulse);
          } else if (glowNodes.has(n.id)) {
            TMP_HL.copy(base).lerp(WHITE, pulse);
          } else if (additiveGlow) {
            TMP_HL.copy(base);
          } else {
            TMP_HL.copy(base).multiplyScalar(FOCUS_DIM);
          }
          posMeshRef.current.setColorAt(i, TMP_HL);
        });
        if (posMeshRef.current.instanceColor)
          posMeshRef.current.instanceColor.needsUpdate = true;
      } else if (prevActiveRef.current) {
        positionNodes.forEach((_, i) =>
          posMeshRef.current.setColorAt(i, posBaseColors[i]),
        );
        if (posMeshRef.current.instanceColor)
          posMeshRef.current.instanceColor.needsUpdate = true;
      }
    }

    // --- Interception mesh ---
    if (intMeshRef.current && interceptionNodes.length > 0) {
      const maxIdx = pos.length / 3;
      interceptionNodes.forEach((n, i) => {
        const idx = layout.nodeIndex.get(n.id);
        if (idx === undefined || idx >= maxIdx) return;
        TMP_POS.set(pos[idx * 3], pos[idx * 3 + 1], pos[idx * 3 + 2]);
        TMP_SCALE.setScalar(radiusFor(n, sizeBy) * boostOf(n.id));
        TMP_QUAT.identity();
        TMP_MAT.compose(TMP_POS, TMP_QUAT, TMP_SCALE);
        intMeshRef.current.setMatrixAt(i, TMP_MAT);
      });
      intMeshRef.current.instanceMatrix.needsUpdate = true;

      if (hasActive) {
        interceptionNodes.forEach((n, i) => {
          const base = intBaseColors[i];
          if (selected === n.id) {
            TMP_HL.copy(base).lerp(WHITE, selPulse);
          } else if (glowNodes.has(n.id)) {
            TMP_HL.copy(base).lerp(WHITE, pulse);
          } else if (additiveGlow) {
            TMP_HL.copy(base);
          } else {
            TMP_HL.copy(base).multiplyScalar(FOCUS_DIM);
          }
          intMeshRef.current.setColorAt(i, TMP_HL);
        });
        if (intMeshRef.current.instanceColor)
          intMeshRef.current.instanceColor.needsUpdate = true;
      } else if (prevActiveRef.current) {
        interceptionNodes.forEach((_, i) =>
          intMeshRef.current.setColorAt(i, intBaseColors[i]),
        );
        if (intMeshRef.current.instanceColor)
          intMeshRef.current.instanceColor.needsUpdate = true;
      }
    }

    prevActiveRef.current = hasActive;
  });

  const onPointerOver = (e: ThreeEvent<PointerEvent>, list: GraphNode[]) => {
    // Press-and-hold orbit/pan gesture: suppress hover from the very
    // first frame of the press (isPressed) — not just after the drag
    // threshold trips. Otherwise the user gets a flash of hover state
    // on whichever node the cursor first sweeps past during a pan.
    if (dragState.isPressed) return;
    if (e.instanceId == null) return;
    e.stopPropagation();
    const id = list[e.instanceId]?.id;
    if (id) {
      setHover(id);
      document.body.style.cursor = 'pointer';
    }
  };
  const onPointerOut = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    clearHoverSoon();
    document.body.style.cursor = 'default';
  };
  const onClick = (e: ThreeEvent<MouseEvent>, list: GraphNode[]) => {
    // If the user was just dragging, treat the pointerup as the END of a
    // drag, not a click. Without this, releasing a drag over any node
    // would select it.
    if (dragState.isDragging) return;
    if (e.instanceId == null) return;
    e.stopPropagation();
    const n = list[e.instanceId];
    if (!n) return;
    // Re-clicking the already-selected node is a no-op. It used to
    // toggle the in-scene summary card; that card is gone (detail lives
    // in the side panel only), so there is nothing left to toggle and
    // re-dispatching the same selection would only churn history.
    if (selectedIdRef.current === n.id) return;
    if (n.kind === 'position') setSelection({ kind: 'position', id: n.id });
    else if (n.kind === 'move') setSelection({ kind: 'move', id: n.id });
    else if (n.kind === 'interception') {
      const parts = n.id.split(':');
      setSelection({ kind: 'counter', id: parts[2], moveId: parts[1] });
    }
  };

  return (
    <>
      {positionNodes.length > 0 && (
        <instancedMesh
          ref={posMeshRef}
          args={[sphereGeom, sphereMat, positionNodes.length]}
          frustumCulled={false}
          onPointerOver={(e) => onPointerOver(e, positionNodes)}
          onPointerOut={onPointerOut}
          onClick={(e) => onClick(e, positionNodes)}
        />
      )}

      {interceptionNodes.length > 0 && (
        <instancedMesh
          ref={intMeshRef}
          args={[intGeom, intMat, interceptionNodes.length]}
          frustumCulled={false}
          // renderOrder + raycaster priority: interception spheres sit
          // ON the bezier arc, co-located with the underlying edge
          // segment. Without this hint the raycaster picks whichever
          // geometry it hits first — which is sometimes the line. Lift
          // the mesh's renderOrder so React-three's pointer dispatch
          // prefers it when distances are within rounding error.
          renderOrder={2}
          onPointerOver={(e) => {
            // Stop here so the edge underneath doesn't ALSO fire its
            // hover-preview / sleeve-fan logic. This is the "priority
            // click target" the user asked for.
            e.stopPropagation();
            onPointerOver(e, interceptionNodes);
          }}
          onPointerOut={onPointerOut}
          onClick={(e) => {
            e.stopPropagation();
            onClick(e, interceptionNodes);
          }}
        />
      )}

      {positionNodes
        .filter((n) => n.isBridge)
        .map((n) => (
          <BridgeRing
            key={n.id}
            node={n}
            layout={layout}
            glowNodes={glowNodes}
            clusterBy={clusterBy}
            positionsById={positionsById}
            movesById={movesById}
          />
        ))}
    </>
  );
}

function BridgeRing({
  node,
  layout,
  glowNodes,
  clusterBy,
  positionsById,
  movesById,
}: {
  node: GraphNode;
  layout: ForceLayout;
  glowNodes: Set<string>;
  clusterBy: ClusterBy;
  positionsById?: Map<string, Position>;
  movesById?: Map<string, Move>;
}) {
  const ringSizeBy = useAppStore((s) => s.sizeBy);
  // Tint the ring with this node's cluster color when clustering is active,
  // so the ring visually echoes the node's cluster membership rather than
  // staying at the default white-ish BRIDGE_RING.
  const ringTint = useMemo(() => {
    if (clusterBy === 'none' || !positionsById || !movesById) return BRIDGE_RING;
    const cid = clusterIdFor(node, clusterBy, positionsById, movesById);
    if (!cid) return BRIDGE_RING;
    if (CLUSTER_COLORS[cid]) return CLUSTER_COLORS[cid];
    if (HASHED_CLUSTER_TYPES.has(clusterBy)) {
      return clusterColorForId(cid, clusterBy as 'family' | 'concept');
    }
    return BRIDGE_RING;
  }, [node, clusterBy, positionsById, movesById]);
  const ref = useRef<THREE.Mesh>(null!);
  const matRef = useRef<THREE.MeshBasicMaterial>(null!);
  useFrame(({ camera }) => {
    if (!layout.posRef.current || !ref.current) return;
    const idx = layout.nodeIndex.get(node.id);
    if (idx === undefined) return;
    const pos = layout.posRef.current;
    ref.current.position.set(pos[idx * 3], pos[idx * 3 + 1], pos[idx * 3 + 2]);
    ref.current.lookAt(camera.position);
    // Deliberately NOT scaled by boostOf(): the ring holds its resting
    // size while the sphere swells into it, so a selected node grows to
    // fill its own ring and the ring disappears behind it. Scaling the
    // ring too keeps it visible and loses that "absorbs the ring" read.

    // Fade the ring along with the node when there's an active set this
    // ring's node isn't part of. Both dimmed values derive from the same
    // FOCUS_DIM, so the two can never disagree.
    if (matRef.current) {
      const dim = glowNodes.size > 0 && !glowNodes.has(node.id);
      matRef.current.opacity = dim
        ? BRIDGE_RING_OPACITY_DIMMED
        : BRIDGE_RING_OPACITY_ACTIVE;
    }
  });
  // Own read — BridgeRing is its own component, and the ring must track
  // the sphere it surrounds.
  const r = radiusFor(node, ringSizeBy) * 1.4;
  return (
    <mesh ref={ref} raycast={() => null}>
      <ringGeometry args={[r * 0.95, r * 1.08, 32]} />
      <meshBasicMaterial
        ref={matRef}
        color={ringTint}
        transparent
        opacity={BRIDGE_RING_OPACITY_ACTIVE}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}
